/**
 * Utility functions for link type detection and handling
 */

import { getBaseUrl } from '../../../lib/utils/getBaseUrl';

// Link type enumeration
export enum LinkType {
  INTERNAL = 'internal',
  AFFILIATE = 'affiliate',
  EXTERNAL = 'external'
}

/**
 * Determines the type of link based on the URL
 */
export function getLinkType(href: string): LinkType {
  if (isAffiliateLink(href)) {
    return LinkType.AFFILIATE;
  }
  
  if (isExternalLink(href)) {
    return LinkType.EXTERNAL;
  }
  
  return LinkType.INTERNAL;
}

/**
 * Checks if a URL is an internal link (same domain as current domain)
 */
export function isInternalLink(href: string): boolean {
  // If it's a relative path, it's internal
  if (href.startsWith('/') && !href.startsWith('//')) {
    return true;
  }
  
  try {
    const currentBaseUrl = getBaseUrl();
    const url = new URL(href, currentBaseUrl);
    const baseUrl = new URL(currentBaseUrl);
    return url.hostname === baseUrl.hostname;
  } catch {
    // If URL parsing fails, treat as internal if it starts with /
    return href.startsWith('/') && !href.startsWith('//');
  }
}

/**
 * Checks if a URL is an affiliate link (starts with /go/ or /affiliate-link/)
 */
export function isAffiliateLink(href: string): boolean {
  return href.startsWith('/go/') || href.startsWith('/affiliate-link/');
}

/**
 * Checks if a URL is an external link
 */
export function isExternalLink(href: string): boolean {
  return !isInternalLink(href);
}

/**
 * Extracts the affiliate identifier from an affiliate path
 */
export function extractAffiliateId(affiliatePath: string): string {
  return affiliatePath.replace('/go/', '').replace('/affiliate-link/', '');
}

/**
 * Validates if a URL is properly formatted
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Cleans base URL by removing hosting and website links
 */
export function cleanBaseUrl(url: string): string {
  const hostingLink = process.env.HOSTING_LINK;
  const websiteLink = process.env.WEBSITE_LINK;
  const currentBaseUrl = getBaseUrl();

  if (hostingLink && url.startsWith(hostingLink)) {
    return url.replace(hostingLink, '');
  }
  if (websiteLink && url.startsWith(websiteLink)) {
    return url.replace(websiteLink, '');
  }
  if (url.startsWith(currentBaseUrl)) {
    return url.replace(currentBaseUrl, '');
  }
  return url;
}

/**
 * Cleans malformed URLs by removing "article" text and fixing URL structure
 * Handles URLs like: /om-aksjekunnskaparticle/nb-cookie-policy/ -> /om-aksjekunnskap/cookie-policy/
 */
export function cleanMalformedUrl(url: string): string {
  // Remove "article" text that appears in the middle of URLs
  // Pattern: /categoryarticle/ -> /category/
  const cleanedUrl = url.replace(/([^/]+)article\//g, '$1/');
  
  return cleanedUrl;
}

/**
 * Checks if a URL is a mailto link
 */
export function isMailto(href: string): boolean {
  return href.toLowerCase().startsWith('mailto:');
}

/**
 * Maps a pathname to an app route (currently returns as-is)
 */
export function mapToAppRoute(pathname: string): string {
  return pathname;
}

/**
 * Adds language prefix to URL if WP_LANGUAGE is not "sv"
 * Example: "no-go-here" becomes "go-here" when WP_LANGUAGE = "no"
 */
export function addLanguagePrefix(pathname: string): string {
  const wpLanguage = process.env.WP_LANGUAGE;
  return `${wpLanguage}-${pathname}`;
}

/**
 * Removes language prefix from URL if WP_LANGUAGE is not "sv"
 * Example: "no-go-here" becomes "go-here" when WP_LANGUAGE = "no"
 * Also handles language prefixes in the middle of paths: "/category/nb-page/" -> "/category/page/"
 */
export function removeLanguagePrefix(href: string): string {
  const wpLanguage = process.env.WP_LANGUAGE;
  
  // Only process if WP_LANGUAGE is set and not "sv"
  if (!wpLanguage || wpLanguage === 'sv') {
    return href;
  }

  // Create the language prefix pattern (e.g., "nb-")
  const languagePrefix = `${wpLanguage}-`;
  
  // Remove language prefix from the beginning of the URL
  if (href.startsWith(languagePrefix)) {
    href = href.substring(languagePrefix.length);
  }
  
  // Remove language prefix from anywhere in the path segments
  // Pattern: /segment/nb-other/ -> /segment/other/
  const pathSegments = href.split('/');
  const cleanedSegments = pathSegments.map(segment => {
    if (segment.startsWith(languagePrefix)) {
      return segment.substring(languagePrefix.length);
    }
    return segment;
  });
  
  return cleanedSegments.join('/');
}

/**
 * Ensures internal links have the correct trailing slash based on environment variable
 */
export function normalizeInternalLink(href: string): string {
  // Only process internal links
  if (!isInternalLink(href)) {
    return href;
  }

  // Skip if it's a file with extension or query parameters
  if (href.includes('.') || href.includes('?')) {
    return href;
  }

  const shouldHaveTrailingSlash = process.env.WEBSITE_TRAILING_SLASH === 'true';
  const hasTrailingSlash = href.endsWith('/');
  const isRoot = href === '/';

  // Don't modify root path
  if (isRoot) {
    return href;
  }

  // Add trailing slash if required and not present
  if (shouldHaveTrailingSlash && !hasTrailingSlash) {
    return href + '/';
  }

  // Remove trailing slash if not required and present
  if (!shouldHaveTrailingSlash && hasTrailingSlash) {
    return href.slice(0, -1);
  }

  return href;
}

/**
 * Comprehensive helper function for all slug operations with language prefix handling
 * @param originalSlug - The original slug (with or without prefix)
 * @returns Object containing all slug variations and utilities
 */
export function processSlug(originalSlug: string) {
  const prefixedSlug = addLanguagePrefix(originalSlug);
  const cleanSlug = removeLanguagePrefix(originalSlug);
  
  return {
    // Original input
    original: originalSlug,
    
    // Clean version (without prefix)
    clean: cleanSlug,
    
    // Prefixed version
    prefixed: prefixedSlug,
    
    // WordPress API usage
    forWordPress: process.env.WEBSITE_LOCALE_PREFIX === 'true' ? prefixedSlug : originalSlug,
    
    // Frontend URL usage (always clean)
    forUrl: cleanSlug,
    
    // Utility methods
    hasPrefix: originalSlug !== cleanSlug,
    needsRedirect: originalSlug !== cleanSlug
  };
}
